/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

export function sortByCreatedAt<T extends { createdAt: string }>(
  array: T[],
  revert?: boolean
): T[] {
  return [...array].slice().sort((a: T, b: T) => {
    const B: Date = new Date(b.createdAt);
    const A: Date = new Date(a.createdAt);
    return revert ? Number(A) - Number(B) : Number(B) - Number(A);
  });
}

export function reverse<T>(array: T[]): T[] {
  return [...array].reverse();
}

export function getLastItem<T>(list: T[]): T {
  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  return list.at(-1);
}

export function getFirstItem<T>(list: T[]): T {
  return list[0];
}

export function sort<T>(
  array: T[],
  compareFunction?: (a: T, b: T) => number
): T[] {
  return [...array].sort(compareFunction);
}

export function allEqual<T>(array: T[], value: T): boolean {
  return [...array].every((v) => v === value);
}

export function generateNumericArray(length: number): number[] {
  return [...Array.from({ length }).keys()];
}

export function replace<T>(array: T[], newValue: T, compareBy: keyof T): T[] {
  return array.map((item) =>
    item[compareBy] === newValue[compareBy] ? newValue : item
  );
}

type NonEmptyArray<T> = readonly [T, ...(readonly T[])];

/**
 * It is more descriptive than a manual length check,
 * it will also make sure that you can safely access the first element after the check when running with `noUncheckedIndexedAccess`
 *
 * It uses a user-defined type guard, transforming the Array into a variadic Tuple where the first Element is always defined if the function returns true.
 * This is similar to the `NonEmptyList` type in other languages like Haskell or Scala
 */
export const isNonEmpty = <T>(
  array: readonly T[] | undefined
): array is NonEmptyArray<T> => array != null && array.length > 0;

/**
 * Extracts the type of the last element of an array.
 * Use-case: Defining the return type of functions that extract the last element of an array, for example [`lodash.last`](https://lodash.com/docs/4.17.15#last).
 * @example
 *   ```
 *   import {LastArrayElement} from 'type-fest';
 *   declare function lastOf<V extends readonly any[]>(array: V): LastArrayElement<V>;
 *   ```
 */
type LastArrayElement<ValueType extends readonly unknown[]> =
  ValueType extends readonly [infer ElementType]
    ? ElementType
    : ValueType extends readonly [infer _, ...infer Tail]
    ? LastArrayElement<Tail>
    : ValueType extends readonly (infer ElementType)[]
    ? ElementType
    : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lastOf = <V extends readonly any[]>(
  array: V
): LastArrayElement<V> | undefined => {
  return isNonEmpty(array) ? array[length - 1] : undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const firstOf = <V extends readonly any[]>(
  array: V
): V[number] | undefined => {
  return isNonEmpty(array) ? array[0] : undefined;
};

export function setDifference<T>(arrayA: T[], arrayB: T[]) {
  const setB = new Set(arrayB);

  return arrayA.filter((x) => !setB.has(x));
}

export function setIntersection<T>(arrayA: T[], arrayB: T[]) {
  const setB = new Set(arrayB);

  return arrayA.filter((x) => setB.has(x));
}

export function setUnion<T>(arrayA: T[], arrayB: T[]) {
  return [...new Set([...arrayA, ...arrayB])];
}

export function setAdjust<T>(
  arrayA: T[],
  arrayB: T[],
  type: "difference" | "union"
) {
  return type === "union"
    ? setUnion(arrayA, arrayB)
    : setDifference(arrayA, arrayB);
}

export const isLastIndex = <T>(list: T[], index: number): boolean =>
  index === list.length - 1;

export function hasAllCommonItems<T extends number | string>(
  target: T[],
  input: T[]
): boolean {
  return (
    target.length === input.length &&
    input.every((item) => target.includes(item))
  );
}

export function intersection<T extends number | string>(
  array1: T[],
  array2: T[]
): T[] {
  return array1.filter((x) => !array2.includes(x));
}

export const unique = <T extends { [key: string]: unknown }>(
  array: T[],
  key: string
): T[] => [...new Map(array.map((item) => [item[key], item])).values()];

export function includes<T extends U, U>(
  array: readonly T[],
  element: U
): element is T {
  return array.includes(element as T);
}
